// @ts-check
/**
 * Custom hook that provides a state variable and a ref that are always in sync.
 * 
 * The state variable can be used to trigger re-renders, while the ref can be used immediately 
 * without waiting for a re-render. This is useful for tracking values that need to be read immediately after being set,
 * such as flags indicating whether an image has been selected for upload.
 * 
 * Usage example:
 *  const [flag, flagRef, setFlag] = useSyncedRefState(false);
 *  setFlag(true); // updates both state and ref
 *  console.log(flagRef.current); // always has the latest value but also causes re-renders when setFlag is called so screens update correctly
 * 
 * @param {any} initialValue - The initial value for the state and ref.
 * 
 * @returns {[any, React.MutableRefObject<any>, function]} - An array containing the state variable, the ref, and a setter function.
 * 
 */
import { useState, useRef, useCallback } from 'react';

function useRefState(initialValue = false) {
  const [value, setValue] = useState(initialValue);
  const ref = useRef(value);

  const setBoth = useCallback((newValue) => {
    ref.current = newValue;
    setValue(newValue);
  }, []);

  return [value, ref, setBoth];
}

export default useRefState;